'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useCallback, useId } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { submitButtonBox } from '@/uiComposites/interactive/callToAction';
import { textBlock } from '@/uiPrimitives/typography/text';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { hiddenAboveMQ640px, hiddenBelowMQ640px } from '@/uiPrimitives/utility/display.css';
import { icon } from '@/uiPrimitives/media/icon';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { pageGrid } from '@/uiPrimitives/layout/pageGrid/pageGrid';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { getSearchURL, searchTerm } from '@/globals/dataCollector/searchDataCollector/functions/getSearchURL';
import { getSearchState } from '@/globals/dataCollector/searchDataCollector/functions/getSearchState';
import { pushSearchResultEvent } from '@/globals/analytics/events/pushSearchEvent';
import { usePageHistory } from '@/globals/pageWrappers/usePageHistory';
import { useSearchHistory } from './useSearchHistory';
import { translateInline } from '@/globals/translate/translateInline';
import { HeadingTextBlock } from '@/uiComposites/typography/headings';
export const SearchFormClient = () => {
  const $ = _c(36);
  const router = useDOPRouter();
  const id = useId();
  const searchParams = useSearchParams();
  const [pageHistory] = usePageHistory();
  const searchHistory = useSearchHistory();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(searchTerm) || "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const term = t0;
  let t1;
  if ($[2] !== term) {
    t1 = input => {
      if (term === "") {
        requestAnimationFrame(() => {
          input.focus();
        });
      }
      if (term !== input.value) {
        input.value = term;
      }
      return _temp;
    };
    $[2] = term;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const focusInput = t1;
  let t2;
  if ($[4] !== pageHistory || $[5] !== router || $[6] !== searchHistory || $[7] !== searchParams || $[8] !== term) {
    t2 = value => {
      const searchState = getSearchState(searchParams);
      searchState.term = value;
      if (term !== value) {
        searchState.facetSlug = "all";
        searchState.page = 1;
      }
      const newUrl = getSearchURL(searchState);
      const routerBehaviorOnSearch = term ? "push" : "replace";
      router[routerBehaviorOnSearch](newUrl, {
        scroll: false
      });
      pushSearchResultEvent({
        searchState,
        searchHistory,
        pageHistory
      });
    };
    $[4] = pageHistory;
    $[5] = router;
    $[6] = searchHistory;
    $[7] = searchParams;
    $[8] = term;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSearch = t2;
  let t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(pageGrid({}), box({
      backgroundColor: "negativeBlueEmphasis"
    }));
    t4 = classes(stack({
      gap: "3 | h1"
    }), box({
      paddingBlock: "5 | betweenParagraphs"
    }));
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[12] !== handleSearch) {
    t5 = e => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const value_0 = formData.get(searchTerm);
      if (typeof value_0 === "string") {
        handleSearch(value_0);
      }
    };
    $[12] = handleSearch;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = addTestSelector("uiSearchLabel");
    t7 = translateInline({
      en: "Search Business.gov.nl",
      nl: "Zoeken op Ondernemersplein"
    });
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== id) {
    t8 = <HeadingTextBlock headingLevel="h1" size="h2"><label htmlFor={id} {...t6}>{t7}</label></HeadingTextBlock>;
    $[16] = id;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = classes(shelf({
      gridTemplateColumns: "minmax(0, 1fr) auto"
    }), zIndex({
      isolation: "isolate"
    }));
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classes(textBlock({
      color: "positive:noHover"
    }), box({
      paddingBlock: "3 | h1",
      paddingInline: indentSize,
      backgroundColor: "negative",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "positive"
    }), zIndex({
      "zIndex:focusVisible": "1"
    }));
    t11 = addTestSelector("uiSearchInput");
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] !== focusInput || $[22] !== id || $[23] !== term) {
    t12 = <input type="search" name={searchTerm} ref={focusInput} autoCapitalize="none" autoComplete="off" id={id} defaultValue={term} className={t10} {...t11} />;
    $[21] = focusInput;
    $[22] = id;
    $[23] = term;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classes(submitButtonBox({}));
    t14 = addTestSelector("uiSearchSubmitBtn");
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  let t15;
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span aria-hidden={true} className={classes(textBlock({}), hiddenBelowMQ640px)}>{translateInline({
        en: "Search",
        nl: "Zoeken"
      })}</span>;
    t16 = classes(occupy({
      inlineSize: "capSize"
    }));
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <button type="submit" className={t13} {...t14}>{t15}<span className={t16}><i role="img" aria-label={translateInline({
          en: "Search",
          nl: "Zoeken"
        })} className={classes(icon({
          icon: searchIcon,
          blockSize: "3 | h1"
        }), hiddenAboveMQ640px)} /></span></button>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t12) {
    t18 = <div className={t9}>{t12}{t17}</div>;
    $[30] = t12;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t18 || $[33] !== t5 || $[34] !== t8) {
    t19 = <div className={t3}><form className={t4} onSubmit={t5}>{t8}{t18}</form></div>;
    $[32] = t18;
    $[33] = t5;
    $[34] = t8;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
};
function _temp() {}